/**
 * Wellness Report Generator - Holistic Cross-Pillar Reports
 * Generates comprehensive wellness reports combining nutrition,
 * health, movement, sleep, energy, and mindfulness data.
 *
 * For the Coach Portal dashboard - follows WeeklyReportGenerator patterns
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

interface WellnessReportGeneratorProps {
  user: any;
  onBack: () => void;
  coachMode?: boolean;
  clientId?: number;
}

type SophisticationLevel = 'beginner' | 'intermediate' | 'advanced';

interface PillarSummary {
  nutrition: { daysLogged: number; deficiencies: number };
  health: { daysLogged: number; avgWellness: string | null; topSymptoms: Array<{ symptom: string; occurrences: number; avgSeverity: string }> };
  movement: { workouts: number; avgSteps: number; habitRate: string | null };
  sleep: { daysLogged: number; avgQuality: string | null; avgDuration: string };
  energy: { logsCount: number; avgLevel: string | null; peakTime: string | null };
  mindfulness: { sessions: number; meditationMinutes: number };
}

interface CrossPillarInsight {
  type: string;
  title: string;
  description: string;
  confidence: string;
}

interface WellnessReport {
  reportId: string;
  reportText: string;
  period: { start: string; end: string; daysLogged: number; totalMeals: number };
  pillars: PillarSummary;
  crossPillarInsights: CrossPillarInsight[];
  metrics: {
    overallScore: number | null;
    pillarScores: Record<string, number | null>;
    activePillars: number;
  };
  sophisticationLevel: string;
  generatedAt: string;
}

export const WellnessReportGenerator: React.FC<WellnessReportGeneratorProps> = ({ user, onBack, coachMode, clientId }) => {
  const [sophisticationLevel, setSophisticationLevel] = useState<SophisticationLevel>('intermediate');
  const [generatedReport, setGeneratedReport] = useState<WellnessReport | null>(null);
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

  // Fetch CSRF token on mount
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        let sessionIdValue = localStorage.getItem('session_id');
        if (!sessionIdValue) {
          sessionIdValue = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('session_id', sessionIdValue);
        }
        const response = await fetch(`${API_URL}/api/csrf-token`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Session-Id': sessionIdValue
          }
        });
        const data = await response.json();
        if (data.success && (data.csrf_token || data.csrfToken)) {
          setCsrfToken(data.csrf_token || data.csrfToken);
          setSessionId(sessionIdValue);
        }
      } catch (err) {
        console.error('Failed to fetch CSRF token:', err);
      }
    };
    fetchCsrfToken();
  }, [API_URL]);

  // Fetch past wellness reports
  const { data: pastReports, refetch: refetchReports } = useQuery({
    queryKey: coachMode ? ['wellness-reports', 'client', clientId] : ['wellness-reports', user?.id],
    queryFn: async () => {
      const url = coachMode && clientId
        ? `${API_URL}/api/wellness-reports?userId=${clientId}&limit=5`
        : `${API_URL}/api/wellness-reports?limit=5`;

      const token = localStorage.getItem('auth_token');
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!response.ok) throw new Error('Failed to fetch wellness reports');
      const data = await response.json();
      return data.reports || [];
    },
    enabled: coachMode ? !!clientId : !!user?.id
  });

  // Fetch data preview
  const { data: previewData } = useQuery({
    queryKey: coachMode ? ['wellness-preview', clientId] : ['wellness-preview', user?.id],
    queryFn: async () => {
      const url = coachMode && clientId
        ? `${API_URL}/api/wellness-reports/preview?userId=${clientId}`
        : `${API_URL}/api/wellness-reports/preview`;

      const token = localStorage.getItem('auth_token');
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.success ? data.preview : null;
    },
    enabled: coachMode ? !!clientId : !!user?.id
  });

  // Generate new report mutation
  const generateMutation = useMutation({
    mutationFn: async (level: SophisticationLevel) => {
      const requestBody: any = { sophisticationLevel: level };
      if (coachMode && clientId) {
        requestBody.userId = clientId;
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/wellness-reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken,
          'X-Session-Id': sessionId,
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate wellness report');
      }

      return await response.json();
    },
    onSuccess: (report) => {
      setGeneratedReport(report);
      refetchReports();
    }
  });

  const handleGenerate = () => {
    generateMutation.mutate(sophisticationLevel);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Pillar data availability indicators
  const pillars = previewData ? [
    { name: 'Nutrition', icon: '🍎', available: previewData.nutrition?.daysLogged > 0, detail: `${previewData.nutrition?.daysLogged || 0} days` },
    { name: 'Health Notes', icon: '💚', available: previewData.health?.daysLogged > 0, detail: `${previewData.health?.daysLogged || 0} days` },
    { name: 'Movement', icon: '🏃', available: previewData.movement?.workouts > 0, detail: `${previewData.movement?.workouts || 0} workouts` },
    { name: 'Sleep', icon: '😴', available: previewData.sleep?.daysLogged > 0, detail: `${previewData.sleep?.daysLogged || 0} nights` },
    { name: 'Energy', icon: '⚡', available: previewData.energy?.logsCount > 0, detail: `${previewData.energy?.logsCount || 0} logs` },
    { name: 'Mindfulness', icon: '🧘', available: previewData.mindfulness?.sessions > 0, detail: `${previewData.mindfulness?.sessions || 0} sessions` },
  ] : [];

  const activePillarCount = pillars.filter(p => p.available).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-2"
          >
            ← Back to Reports
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Holistic Wellness Report</h2>
          <p className="text-gray-600 mt-1">
            AI-powered cross-pillar analysis: nutrition, health, movement, sleep, energy & mindfulness
          </p>
        </div>
      </div>

      {/* Data Availability */}
      {pillars.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Data Available (Last 7 Days)</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {pillars.map(pillar => (
                <div
                  key={pillar.name}
                  className={`p-3 rounded-lg text-center ${
                    pillar.available
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-gray-50 border border-gray-200 opacity-60'
                  }`}
                >
                  <div className="text-2xl mb-1">{pillar.icon}</div>
                  <div className="text-sm font-medium text-gray-900">{pillar.name}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {pillar.available ? pillar.detail : 'No data'}
                  </div>
                  <div className="text-xs mt-1">
                    {pillar.available ? '✅' : '—'}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-3 text-center">
              {activePillarCount >= 3
                ? `${activePillarCount} pillars active — great coverage for cross-pillar insights!`
                : `${activePillarCount} pillar${activePillarCount !== 1 ? 's' : ''} active — track more areas for richer insights.`}
            </p>
          </div>
        </div>
      )}

      {/* Generator Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Generate Wellness Report</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {/* Sophistication Level Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Report Style</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setSophisticationLevel('beginner')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sophisticationLevel === 'beginner'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg font-medium text-gray-900 mb-1">🌱 Simple</div>
                  <div className="text-sm text-gray-600">
                    Easy-to-understand insights with actionable wellness tips.
                  </div>
                </button>

                <button
                  onClick={() => setSophisticationLevel('intermediate')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sophisticationLevel === 'intermediate'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg font-medium text-gray-900 mb-1">📊 Balanced</div>
                  <div className="text-sm text-gray-600">
                    Cross-pillar patterns with data-driven recommendations.
                  </div>
                </button>

                <button
                  onClick={() => setSophisticationLevel('advanced')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sophisticationLevel === 'advanced'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg font-medium text-gray-900 mb-1">🔬 Comprehensive</div>
                  <div className="text-sm text-gray-600">
                    Full cross-pillar analysis with scientific context and optimization plan.
                  </div>
                </button>
              </div>
            </div>

            {/* Generate Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={handleGenerate}
                disabled={generateMutation.isPending}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
              >
                {generateMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating Wellness Report...
                  </span>
                ) : (
                  '✨ Generate Holistic Wellness Report'
                )}
              </button>
            </div>

            {/* Error */}
            {generateMutation.isError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-red-800 font-medium">Error Generating Report</div>
                <div className="text-red-600 text-sm mt-1">
                  {generateMutation.error instanceof Error ? generateMutation.error.message : 'Unknown error'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Generated Report Display */}
      {generatedReport && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Holistic Wellness Report</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {formatDate(generatedReport.period.start)} - {formatDate(generatedReport.period.end)} • {generatedReport.period.daysLogged} days
                </p>
              </div>
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                {generatedReport.sophisticationLevel}
              </span>
            </div>
          </div>
          <div className="card-body">
            {/* Pillar Summary Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 p-4 bg-gray-50 rounded-lg mb-6">
              <div className="text-center">
                <div className="text-xl">🍎</div>
                <div className="text-lg font-bold text-green-600">{generatedReport.pillars.nutrition.daysLogged}d</div>
                <div className="text-xs text-gray-600">Nutrition</div>
              </div>
              <div className="text-center">
                <div className="text-xl">💚</div>
                <div className="text-lg font-bold text-emerald-600">{generatedReport.pillars.health.avgWellness || '—'}</div>
                <div className="text-xs text-gray-600">Wellness</div>
              </div>
              <div className="text-center">
                <div className="text-xl">🏃</div>
                <div className="text-lg font-bold text-blue-600">{generatedReport.pillars.movement.workouts}</div>
                <div className="text-xs text-gray-600">Workouts</div>
              </div>
              <div className="text-center">
                <div className="text-xl">😴</div>
                <div className="text-lg font-bold text-indigo-600">{generatedReport.pillars.sleep.avgDuration || '—'}</div>
                <div className="text-xs text-gray-600">Avg Sleep</div>
              </div>
              <div className="text-center">
                <div className="text-xl">⚡</div>
                <div className="text-lg font-bold text-yellow-600">{generatedReport.pillars.energy.avgLevel || '—'}</div>
                <div className="text-xs text-gray-600">Avg Energy</div>
              </div>
              <div className="text-center">
                <div className="text-xl">🧘</div>
                <div className="text-lg font-bold text-purple-600">{generatedReport.pillars.mindfulness.sessions}</div>
                <div className="text-xs text-gray-600">Sessions</div>
              </div>
            </div>

            {/* Cross-Pillar Insights */}
            {generatedReport.crossPillarInsights && generatedReport.crossPillarInsights.length > 0 && (
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="font-medium text-purple-900 mb-2">🔗 Cross-Pillar Insights</h4>
                <div className="space-y-2">
                  {generatedReport.crossPillarInsights.map((insight, i) => (
                    <div key={i} className="flex items-start justify-between text-sm">
                      <div>
                        <span className="font-medium text-purple-800">{insight.title}:</span>{' '}
                        <span className="text-purple-700">{insight.description}</span>
                      </div>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        insight.confidence === 'high' ? 'bg-green-100 text-green-800' :
                        insight.confidence === 'moderate' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {insight.confidence}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overall Score */}
            {generatedReport.metrics?.overallScore && (
              <div className="mb-6 text-center">
                <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl px-6 py-3">
                  <span className="text-sm font-medium text-gray-700">Overall Wellness Score</span>
                  <span className="text-3xl font-bold text-purple-700">{generatedReport.metrics.overallScore}</span>
                  <span className="text-sm text-gray-500">/100</span>
                </div>
              </div>
            )}

            {/* AI-Generated Report Text */}
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                {generatedReport.reportText}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
              <div className="text-xs text-gray-500">
                Generated {new Date(generatedReport.generatedAt).toLocaleString()} •
                {generatedReport.metrics?.activePillars || 0} pillars analyzed •
                Powered by Vertex AI
              </div>
              <button
                onClick={() => setGeneratedReport(null)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Generate Another
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Past Reports */}
      {pastReports && pastReports.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Past Wellness Reports</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {pastReports.map((report: any) => (
                <div
                  key={report.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setGeneratedReport({
                      reportId: report.id,
                      reportText: report.report_text,
                      period: {
                        start: report.period_start,
                        end: report.period_end,
                        daysLogged: 0,
                        totalMeals: 0
                      },
                      pillars: report.metrics?.pillarScores
                        ? {
                            nutrition: { daysLogged: 0, deficiencies: 0 },
                            health: { daysLogged: 0, avgWellness: null, topSymptoms: [] },
                            movement: { workouts: 0, avgSteps: 0, habitRate: null },
                            sleep: { daysLogged: 0, avgQuality: null, avgDuration: '—' },
                            energy: { logsCount: 0, avgLevel: null, peakTime: null },
                            mindfulness: { sessions: 0, meditationMinutes: 0 }
                          }
                        : {} as any,
                      crossPillarInsights: [],
                      metrics: report.metrics || { overallScore: null, pillarScores: {}, activePillars: 0 },
                      sophisticationLevel: report.sophistication_level,
                      generatedAt: report.generated_at
                    });
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {formatDate(report.period_start)} - {formatDate(report.period_end)}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {report.sophistication_level} style
                        {report.metrics?.overallScore ? ` • Score: ${report.metrics.overallScore}/100` : ''}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(report.generated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
